import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  getLinkableIntegrators,
  getLinkedIntegrators,
  linkIntegrator,
  unlinkIntegrator,
} from '../ecosystemPartners.service';
import { resultMessage } from '../../../../service/apiResult';
import InlineAlert from '../../../components/inlineAlert';

const CATEGORY = {
  AREA_BASED: 'Area based',
  CROP_BASED: 'Crop based',
  INITIATIVE_BASED: 'Initiative based',
  FINANCE_BASED: 'Finance based',
};

const accountLabel = (option) =>
  [option.name, option.ecosystemName, CATEGORY[option.ecosystemCategory], option.countryName]
    .filter(Boolean)
    .join(' · ');

/**
 * Initiative approval chain (P2-3): one partner can need two integrator accounts, e.g. an area-based one for
 * its country and an Initiative Based one for its own app. Linking them shows they are one organisation;
 * it does not share any access between them.
 */
export default function LinkedAccounts({ integratorId, cardSx, titleSx, subtitleSx }) {
  const [linked, setLinked] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!integratorId) return;
    setLoading(true);
    const [list, options] = await Promise.all([
      getLinkedIntegrators(integratorId),
      getLinkableIntegrators(integratorId),
    ]);
    if (list.success) setLinked(list.data || []);
    if (options.success) setCandidates(options.data || []);
    if (!list.success || !options.success) {
      setError(resultMessage(list.success ? options : list, 'Could not load the linked accounts.'));
    }
    setLoading(false);
  }, [integratorId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (call) => {
    setSaving(true);
    setError('');
    const res = await call();
    setSaving(false);
    if (res.success) {
      setSelected(null);
      load();
    } else {
      setError(resultMessage(res));
    }
  };

  return (
    <Box sx={cardSx}>
      <Typography sx={titleSx}>Same organisation</Typography>
      <Typography sx={{ ...subtitleSx, mb: 2 }}>
        Other integrator accounts of this partner, for example its area-based and Initiative based accounts.
        Linking is for display only; each account keeps its own access.
      </Typography>

      <InlineAlert message={error} onClose={() => setError('')} sx={{ mb: 2 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Autocomplete
          sx={{ flex: 1, maxWidth: { sm: 560 } }}
          options={candidates}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          getOptionLabel={accountLabel}
          isOptionEqualToValue={(option, value) => option.integratorId === value.integratorId}
          loading={loading}
          noOptionsText="No other integrator accounts"
          renderInput={(params) => <TextField {...params} size="small" label="Integrator account" />}
        />
        <Button
          type="button"
          variant="contained"
          disabled={!selected || saving}
          onClick={() => run(() => linkIntegrator(integratorId, selected.integratorId))}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Icon icon="mdi:link-variant" />}
          sx={{ height: 40, textTransform: 'none', backgroundColor: '#22367A', boxShadow: 'none' }}
        >
          Link
        </Button>
      </Stack>

      {linked.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#999' }}>
          {loading ? 'Loading…' : 'Not linked to any other account'}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {linked.map((account) => (
            <Chip
              key={account.integratorId}
              label={accountLabel(account)}
              onDelete={saving ? undefined : () => run(() => unlinkIntegrator(integratorId, account.integratorId))}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
